const DB_NAME = "ReduxStateDB";
const DB_VERSION = 1;
const STORE_NAME = "reduxState";

const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = event => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, {
          keyPath: "key",
        });
        objectStore.createIndex("timestamp", "timestamp", { unique: false });
      }
    };
  });
};

// Save Redux state to IndexedDB
export const saveReduxState = async state => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    // Save entire state
    await new Promise((resolve, reject) => {
      const request = store.put({
        key: "root",
        state: state,
        timestamp: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn("[ReduxPersist] Error saving state:", error);
  }
};

// Load Redux state from IndexedDB
export const loadReduxState = async () => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get("root");
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.state : null);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn("[ReduxPersist] Error loading state:", error);
    return null;
  }
};

// Clear Redux state from IndexedDB
export const clearReduxState = async () => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    await new Promise((resolve, reject) => {
      const request = store.delete("root");
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn("[ReduxPersist] Error clearing state:", error);
  }
};

// Redux middleware to persist state
export const reduxPersistMiddleware = store => next => action => {
  const result = next(action);
  const state = store.getState();

  // Debounce saves to avoid too many writes
  if (!reduxPersistMiddleware.saveTimeout) {
    reduxPersistMiddleware.saveTimeout = setTimeout(() => {
      saveReduxState(state);
      reduxPersistMiddleware.saveTimeout = null;
    }, 1000); // Save after 1 second of inactivity
  } else {
    clearTimeout(reduxPersistMiddleware.saveTimeout);
    reduxPersistMiddleware.saveTimeout = setTimeout(() => {
      saveReduxState(state);
      reduxPersistMiddleware.saveTimeout = null;
    }, 1000);
  }

  return result;
};

reduxPersistMiddleware.saveTimeout = null;
